import { AdCreative, PacketStrategyGroup } from '@marketing-milk/interfaces'
import { StrategyView } from '../editor/sections/joinStrategy'

/**
 * One stop in the review walkthrough. Themes are steps in their own right rather
 * than a property of the ad that follows them: a packet ships ~50 ads across a
 * handful of themes, and a client arrowing through them has no other signal that
 * the reasoning changed underneath them.
 */
export type ReviewStep =
  | { key: string; kind: 'theme'; group: PacketStrategyGroup; adCount: number }
  | { key: string; kind: 'ad'; creative: AdCreative; themeLabel?: string }

export const adStepKey = (creativeID: number) => `ad:${creativeID}`

/**
 * The order the walkthrough runs in, matching what's on screen: each theme's
 * slide, then its ads, then anything ungrouped.
 *
 * The ungrouped bucket gets no slide of its own. It isn't a theme — it means
 * "arrived since the last strategy generation" — so announcing it the way a
 * deliberate grouping is announced would claim reasoning that doesn't exist.
 * An empty theme is skipped entirely rather than producing a slide introducing
 * nothing.
 */
export function buildReviewSteps({ groups, ungrouped }: StrategyView): ReviewStep[] {
  const steps: ReviewStep[] = []

  for (const view of groups) {
    if (view.creatives.length === 0) continue
    steps.push({
      key: `theme:${view.group.id}`,
      kind: 'theme',
      group: view.group,
      adCount: view.creatives.length,
    })
    for (const creative of view.creatives) {
      steps.push({
        key: adStepKey(creative.id),
        kind: 'ad',
        creative,
        themeLabel: view.group.label,
      })
    }
  }

  for (const creative of ungrouped) {
    steps.push({ key: adStepKey(creative.id), kind: 'ad', creative })
  }

  return steps
}

/** Position of an ad within the ads alone, so the counter doesn't number the slides. */
export function adPosition(steps: ReviewStep[], index: number) {
  let seen = 0
  for (let i = 0; i <= index; i++) if (steps[i]?.kind === 'ad') seen++
  return { current: seen, total: steps.filter(step => step.kind === 'ad').length }
}
